import axios from 'axios';
import { getAnalytics, logEvent } from 'firebase/analytics';
import {
    CloseRounded,
    HistoryRounded,
} from '@mui/icons-material';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { sanitizeSearchLanding } from '../utils/searchLanding';

const speakerImageURL = (speakerId) => (
    `https://firebasestorage.googleapis.com/v0/b/twinklepoo/o/photos%2F${encodeURIComponent(speakerId)}.png?alt=media`
);

const searchLandingSkeletonTopicWidths = ['112px', '96px', '128px'];

const hideBrokenImage = (event) => {
    event.currentTarget.style.display = 'none';
};

const SearchLandingSkeleton = () => (
    <div
        className='searchLandingSkeleton'
        role='status'
        aria-live='polite'
        aria-busy='true'
    >
        <span className='searchLandingSkeletonLabel'>
            Loading shows, speakers, and topics.
        </span>

        <section className='searchLandingSection' aria-hidden='true'>
            <div className='searchLandingSectionHeader'>
                <h2 className='searchLandingSectionTitle'>Shows</h2>
            </div>
            <ul className='searchLandingGrid searchLandingPodcastGrid'>
                {Array.from({ length: 3 }, (_, index) => (
                    <li
                        className={[
                            'searchLandingPodcastItem',
                            'searchLandingSkeletonPodcastItem',
                        ].join(' ')}
                        key={`podcast-skeleton-${index}`}
                    >
                        <div className='searchLandingSkeletonPodcast'>
                            <span
                                className={[
                                    'searchLandingSkeletonBlock',
                                    'searchLandingSkeletonPodcastArtwork',
                                ].join(' ')}
                            />
                            <span
                                className={[
                                    'searchLandingSkeletonBlock',
                                    'searchLandingSkeletonPodcastLine',
                                ].join(' ')}
                            />
                            <span
                                className={[
                                    'searchLandingSkeletonBlock',
                                    'searchLandingSkeletonPodcastLine',
                                    'searchLandingSkeletonPodcastLineShort',
                                ].join(' ')}
                            />
                        </div>
                    </li>
                ))}
            </ul>
        </section>

        <section className='searchLandingSection' aria-hidden='true'>
            <div className='searchLandingSectionHeader'>
                <h2 className='searchLandingSectionTitle'>Speakers</h2>
            </div>
            <ul className='searchLandingGrid searchLandingSpeakerGrid'>
                {Array.from({ length: 5 }, (_, index) => (
                    <li
                        className={[
                            'searchLandingSpeakerItem',
                            'searchLandingSkeletonSpeakerItem',
                        ].join(' ')}
                        key={`speaker-skeleton-${index}`}
                    >
                        <div className='searchLandingSkeletonSpeaker'>
                            <span
                                className={[
                                    'searchLandingSkeletonBlock',
                                    'searchLandingSkeletonPortrait',
                                ].join(' ')}
                            />
                            <span
                                className={[
                                    'searchLandingSkeletonBlock',
                                    'searchLandingSkeletonSpeakerLine',
                                ].join(' ')}
                            />
                        </div>
                    </li>
                ))}
            </ul>
        </section>

        <section
            className='searchLandingSection searchLandingTopicsSection'
            aria-hidden='true'
        >
            <div className='searchLandingSectionHeader'>
                <h2 className='searchLandingSectionTitle'>Topics</h2>
            </div>
            <ul className='searchLandingTopics'>
                {searchLandingSkeletonTopicWidths.map((width, index) => (
                    <li key={`topic-skeleton-${index}`}>
                        <span
                            className={[
                                'searchLandingSkeletonBlock',
                                'searchLandingSkeletonTopic',
                            ].join(' ')}
                            style={{ width }}
                        />
                    </li>
                ))}
            </ul>
        </section>
    </div>
);

export const TopSpeakerTopics = ({
    landingData: providedLandingData,
    recentSearches = [],
    onRecentSearchClick = () => {},
    onRecentSearchRemove = () => {},
    onRecentSearchesClear = () => {},
    onTopicSearch,
}) => {
    const analytics = getAnalytics();
    const navigate = useNavigate();
    const hasProvidedLandingData = providedLandingData !== undefined;
    const [landingData, setLandingData] = useState(() => (
        hasProvidedLandingData
            ? sanitizeSearchLanding(providedLandingData)
            : null
    ));
    const [loadState, setLoadState] = useState(
        hasProvidedLandingData ? 'loaded' : 'loading',
    );
    const [reloadToken, setReloadToken] = useState(0);

    useEffect(() => {
        if (hasProvidedLandingData) {
            setLandingData(sanitizeSearchLanding(providedLandingData));
            setLoadState('loaded');
        }
    }, [hasProvidedLandingData, providedLandingData]);

    useEffect(() => {
        if (hasProvidedLandingData || landingData !== null) {
            return undefined;
        }

        let isActive = true;
        setLoadState('loading');
        axios
            .get('/search_landing')
            .then((response) => {
                if (!isActive) {
                    return;
                }

                setLandingData(sanitizeSearchLanding(response.data));
                setLoadState('loaded');
            })
            .catch((error) => {
                console.log(error);
                if (isActive) {
                    setLoadState('error');
                }
            });

        return () => {
            isActive = false;
        };
    }, [
        hasProvidedLandingData,
        landingData,
        reloadToken,
    ]);

    const podcasts = landingData ? landingData.podcasts : [];
    const speakers = landingData ? landingData.speakers : [];
    const topics = landingData ? landingData.topics : [];
    const hasSuggestions = (
        podcasts.length > 0
        || speakers.length > 0
        || topics.length > 0
    );

    const openPodcast = (podcast) => () => {
        logEvent(analytics, 'search_landing_podcast_click', {
            podcast_id: podcast.id,
            podcast_name: podcast.name,
        });
        navigate(`/podcast/${encodeURIComponent(podcast.id)}`);
    };

    const openSpeaker = (speaker) => () => {
        logEvent(analytics, 'search_landing_speaker_click', {
            speaker_id: speaker.speakerId,
            speaker_name: speaker.name,
        });
        navigate(`/speaker/${encodeURIComponent(speaker.speakerId)}`);
    };

    const searchTopic = (topic) => () => {
        logEvent(analytics, 'search_landing_topic_click', {
            topic_query: topic.query,
            topic_label: topic.label,
        });
        if (onTopicSearch) {
            onTopicSearch(topic.query);
            return;
        }
        navigate(`/search/${encodeURIComponent(topic.query)}`);
    };

    const retrySearchLanding = () => {
        setLoadState('loading');
        setLandingData(null);
        setReloadToken((value) => value + 1);
    };

    const suggestionsStatus = loadState === 'loading' ? (
        <SearchLandingSkeleton />
    ) : (
        <div className='searchLandingStatus' role='status'>
            <span>
                {loadState === 'error'
                    ? 'Suggestions are unavailable right now.'
                    : 'No suggestions are available right now.'}
            </span>
            <button
                type='button'
                className='searchLandingRetryButton'
                onClick={retrySearchLanding}
            >
                Try again
            </button>
        </div>
    );

    return (
        <section className='searchLanding' aria-label='Search suggestions'>
            {recentSearches.length > 0 && (
                <section
                    className='searchLandingSection'
                    aria-labelledby='recent-searches-heading'
                >
                    <div className='searchLandingSectionHeader'>
                        <h2
                            className='searchLandingSectionTitle'
                            id='recent-searches-heading'
                        >
                            Recent searches
                        </h2>
                        <button
                            type='button'
                            className='searchLandingClearButton'
                            onClick={onRecentSearchesClear}
                        >
                            Clear
                        </button>
                    </div>
                    <ul className='searchLandingList'>
                        {recentSearches.map((query) => (
                            <li className='searchLandingRecentRow' key={query}>
                                <button
                                    type='button'
                                    className='searchLandingRecentQuery'
                                    onClick={() => onRecentSearchClick(query)}
                                >
                                    <HistoryRounded
                                        aria-hidden='true'
                                        className='searchLandingRowIcon'
                                    />
                                    <span className='searchLandingRowLabel'>
                                        {query}
                                    </span>
                                </button>
                                <button
                                    type='button'
                                    className='searchLandingRemoveButton'
                                    aria-label={`Remove ${query} from recent searches`}
                                    onClick={() => onRecentSearchRemove(query)}
                                >
                                    <CloseRounded aria-hidden='true' />
                                </button>
                            </li>
                        ))}
                    </ul>
                </section>
            )}

            {hasSuggestions ? (
                <>
                    {podcasts.length > 0 && (
                        <section
                            className='searchLandingSection'
                            aria-labelledby='search-podcasts-heading'
                        >
                            <div className='searchLandingSectionHeader'>
                                <h2
                                    className='searchLandingSectionTitle'
                                    id='search-podcasts-heading'
                                >
                                    Shows
                                </h2>
                            </div>
                            <ul className='searchLandingGrid searchLandingPodcastGrid'>
                                {podcasts.map((podcast) => (
                                    <li
                                        className='searchLandingPodcastItem'
                                        key={podcast.id}
                                    >
                                        <button
                                            type='button'
                                            className='searchLandingPodcast'
                                            aria-label={`Open ${podcast.name}`}
                                            onClick={openPodcast(podcast)}
                                        >
                                            <span className='searchLandingPodcastArtwork podcastArtwork'>
                                                <span
                                                    className='searchLandingArtworkFallback'
                                                    aria-hidden='true'
                                                >
                                                    {podcast.name.charAt(0).toUpperCase()}
                                                </span>
                                                {podcast.imageURL && (
                                                    <img
                                                        alt=''
                                                        className='searchLandingPodcastImage'
                                                        decoding='async'
                                                        loading='lazy'
                                                        onError={hideBrokenImage}
                                                        src={podcast.imageURL}
                                                    />
                                                )}
                                            </span>
                                            <span className='searchLandingPodcastName'>
                                                {podcast.name}
                                            </span>
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        </section>
                    )}

                    {speakers.length > 0 && (
                        <section
                            className='searchLandingSection'
                            aria-labelledby='search-speakers-heading'
                        >
                            <div className='searchLandingSectionHeader'>
                                <h2
                                    className='searchLandingSectionTitle'
                                    id='search-speakers-heading'
                                >
                                    Speakers
                                </h2>
                            </div>
                            <ul className='searchLandingGrid searchLandingSpeakerGrid'>
                                {speakers.map((speaker) => (
                                    <li
                                        className='searchLandingSpeakerItem'
                                        key={speaker.speakerId}
                                    >
                                        <button
                                            type='button'
                                            className='searchLandingSpeaker'
                                            aria-label={`Open ${speaker.name}`}
                                            onClick={openSpeaker(speaker)}
                                        >
                                            <span
                                                className={[
                                                    'searchLandingPortrait',
                                                    speaker.speakerId === 'wiki_177840'
                                                        ? 'searchLandingPortraitNolan'
                                                        : '',
                                                ].filter(Boolean).join(' ')}
                                            >
                                                <span
                                                    className='searchLandingPortraitFallback'
                                                    aria-hidden='true'
                                                >
                                                    {speaker.name.charAt(0).toUpperCase()}
                                                </span>
                                                <img
                                                    alt=''
                                                    className={[
                                                        'searchLandingPortraitImage',
                                                        speaker.speakerId === 'wiki_177840'
                                                            ? 'searchLandingPortraitImageNolan'
                                                            : '',
                                                    ].filter(Boolean).join(' ')}
                                                    decoding='async'
                                                    loading='lazy'
                                                    onError={hideBrokenImage}
                                                    src={speakerImageURL(speaker.speakerId)}
                                                />
                                            </span>
                                            <span className='searchLandingSpeakerName'>
                                                {speaker.name}
                                            </span>
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        </section>
                    )}

                    {topics.length > 0 && (
                        <section
                            className='searchLandingSection searchLandingTopicsSection'
                            aria-labelledby='search-topics-heading'
                        >
                            <div className='searchLandingSectionHeader'>
                                <h2
                                    className='searchLandingSectionTitle'
                                    id='search-topics-heading'
                                >
                                    Topics
                                </h2>
                            </div>
                            <ul className='searchLandingTopics'>
                                {topics.map((topic) => (
                                    <li key={topic.query}>
                                        <button
                                            type='button'
                                            className='searchLandingTopicChip'
                                            aria-label={`Search for ${topic.label}`}
                                            onClick={searchTopic(topic)}
                                        >
                                            {topic.label}
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        </section>
                    )}
                </>
            ) : suggestionsStatus}
        </section>
    );
};

export default TopSpeakerTopics;
